import * as Sentry from '@sentry/browser';
import config from 'config';

import { ProductType } from '~/techstyle-shared/react-products';

import generateSkuWithoutSize from './generateSkuWithoutSize';

const cdnUrl = config.get('public.cdn');

const imageSizesObject = config.get('public.brand.imageSizes');

const generateGiftCardImage = ({ product, angle, imageSize }) => {
  const label = product.alias.split(' ');

  if (label.length < 2) {
    Sentry.captureMessage(
      `Error: 'generateSxfCdnImageUrl' could not get label`,
      'warning'
    );
    return null;
  }

  const permalink = product.permalink;

  if (!permalink) {
    Sentry.captureMessage(
      `Error: 'generateSxfCdnImageUrl' could not find permalink for the following product: ${product}`,
      'warning'
    );
    return null;
  }
  return `${cdnUrl}/media/images/products/${label[0]}/${permalink}-${angle}-${imageSizesObject[imageSize].width}x${imageSizesObject[imageSize].height}.jpg`;
};

/**
 * Generates a CDN image URL for Savage X Fenty products with specified dimensions and styling parameters.
 * It constructs URLs based on product SKU, permalink, and brand-specific image size configurations.
 *
 * @param {Object} options - Configuration options for generating the CDN image URL
 * @param {Object} options.product - Product object containing product information
 * @param {string} [options.product.alias] - Product alias (used for SKU generation and gift card labels)
 * @param {string} [options.product.itemNumber] - Product item number (preferred SKU identifier)
 * @param {string} [options.product.item_number] - Alternative item number property name
 * @param {string} [options.product.permalink] - Product permalink (used in URL path)
 * @param {string} [options.product.label] - Product label (fallback for permalink generation)
 * @param {string} [options.product.masterLabel] - Master product label (preferred over label for permalink)
 * @param {number} options.product.productTypeId - Product type ID for special handling (gift certificates, membership)
 * @param {number} [options.modelIndex=0] - Model index for alternative product images (0 for default, >0 for alt images)
 * @param {number|string} [options.angle=1] - Image angle/view (1-5 for numbered angles, 'LAYDOWN' for laydown view)
 * @param {number} [options.imageSize=2] - Image size index from brand configuration (maps to width/height)
 * @param {boolean} [options.withWidthHeight=false] - Whether to return object with url, width, and height properties
 * @returns {string|Object|null} Generated CDN URL string, or object with url/width/height if withWidthHeight=true, or null on error
 */
const generateSxfCdnImageUrl = ({
  product,
  modelIndex = 0,
  angle = 1, // 1-5 or laydown
  imageSize = 2,
  withWidthHeight = false,
}) => {
  if (typeof imageSizesObject[imageSize] === 'undefined') {
    Sentry.captureException(
      `Error: 'generateSxfCdnImageUrl' imageSize ${imageSize} is not valid.`
    );
    return null;
  }

  if (product.productTypeId === ProductType.GIFT_CERTIFICATE) {
    return generateGiftCardImage({
      product,
      angle: 1,
      imageSize,
    });
  } else if (product.productTypeId === ProductType.MEMBERSHIP_FEE_PLACEHOLDER) {
    return `${cdnUrl}${config.get(
      'public.membershipProductImage.thumbnailURL'
    )}`;
  }

  const itemNumber = product.item_number || product.itemNumber;
  const skuWithoutSize = generateSkuWithoutSize({
    alias: product.alias,
    itemNumber,
  });

  if (!skuWithoutSize) {
    Sentry.captureException(
      `Error: 'generateSxfCdnImageUrl' could not find the sku for the following product: ${product}`
    );
    return null;
  }

  let permalink = product.permalink;

  if (!permalink) {
    const useMasterLabel =
      product.masterLabel && product.masterLabel.toUpperCase();
    const useLabel = product.label && product.label.toUpperCase();
    let label = useMasterLabel || useLabel;

    if (label) {
      label = label.replace(/ /g, '-');
      permalink = `${label}-${skuWithoutSize}`;
    } else {
      Sentry.captureMessage(
        `Error: 'generateSxfCdnImageUrl' could not find permalink for the following product: ${product}`,
        'warning'
      );
      return null;
    }
  }

  const url = `${cdnUrl}/media/images/products/${skuWithoutSize.toUpperCase()}/${permalink.toUpperCase()}${
    modelIndex ? `-alt${modelIndex}` : ''
  }-${angle.toString().toUpperCase()}-${imageSizesObject[imageSize].width}x${
    imageSizesObject[imageSize].height
  }.jpg`;

  if (withWidthHeight) {
    return {
      url,
      ...imageSizesObject[imageSize],
    };
  }

  return url;
};

export default generateSxfCdnImageUrl;
